import { AddNoteIcon } from "@xola/icons";
import { flash } from "@xola/ui-kit";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Permission, Traveler } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { IsGranted } from "../../Common/IsGranted";
import { CustomerNoteList } from "../../Common/NoteList";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { CreateCustomerNoteModal } from "./CreateCustomerNoteModal";

export const CustomerNotes = ({ contact }: { contact: Traveler }) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const sellerCustomerNotes = seller.customerNotes;
    const customerNotes = sellerCustomerNotes ? Object.values(sellerCustomerNotes[contact.id] ?? {}) : [];
    const [showAddNoteModal, setShowAddNoteModal] = useState(false);
    const toggleNoteModal = () => setShowAddNoteModal(!showAddNoteModal);

    const { mutateAsync: deleteNoteItem } = useMutation(
        async (noteId: ID) => {
            try {
                await api.delete(`/sellers/${seller.id}/customers/${contact.id}/notes/${noteId}`);
            } catch {
                flash.show({ text: "Error on deleting customer notes", color: "danger" });
            }
        },
        {
            onMutate: (noteId) => {
                if (contact.id) {
                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                    delete seller.customerNotes[contact.id][noteId];
                }
            },
        },
    );

    const onDelete = async (noteId: ID) => {
        await deleteNoteItem(noteId);
        invalidate("users", contact.id);
    };

    return (
        <div className="pt-10">
            <div className="mb-5 flex items-center justify-between">
                <h3 className="mb-5 font-bold">Customer Notes</h3>
                <IsGranted permissions={Permission.MODIFY_CUSTOMER_DETAILS}>
                    <SecondaryButton size="small" icon={<AddNoteIcon />} onClick={toggleNoteModal}>
                        Add Note
                    </SecondaryButton>
                </IsGranted>
            </div>

            <div>
                {customerNotes.length > 0 ? (
                    <CustomerNoteList
                        userId={contact.id}
                        readOnly={!isGranted(Permission.MODIFY_CUSTOMER_DETAILS)}
                        onDelete={onDelete}
                    />
                ) : (
                    <p>No Customer Notes</p>
                )}
            </div>

            <CreateCustomerNoteModal isOpen={showAddNoteModal} customerId={contact.id} onClose={toggleNoteModal} />
        </div>
    );
};
